import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { specLabel } from "@/lib/specialisms";
import { toast } from "sonner";
import { Briefcase, CheckCircle2, Plus, Trash2, Users, XCircle } from "lucide-react";

const EMPTY = { title: "", description: "", deliverables: "", pay: "", deadline: "", specialisms: [], brand_name: "", example_links: ["", "", ""] };

const APP_BADGE = {
    applied: "bg-yc-cream",
    approved: "bg-yc-blue text-white",
    rejected: "bg-yc-ink text-white",
};

const AdminJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [specialisms, setSpecialisms] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [active, setActive] = useState(null); // {job, applicants}
    const [busy, setBusy] = useState("");

    const load = async (keepJobId) => {
        const r = await api.get("/admin/jobs");
        setJobs(r.data);
        if (keepJobId) openJob(keepJobId);
    };
    useEffect(() => {
        load();
        api.get("/creator/specialisms").then((r) => setSpecialisms(r.data)).catch(() => {});
    }, []);

    const openJob = async (jobId) => {
        const r = await api.get(`/admin/jobs/${jobId}`);
        setActive(r.data);
    };

    const toggleSpec = (s) => setForm((f) => ({
        ...f, specialisms: f.specialisms.includes(s) ? f.specialisms.filter((x) => x !== s) : [...f.specialisms, s],
    }));

    const create = async () => {
        if (!form.title.trim()) { toast.error("Job title is required"); return; }
        setSaving(true);
        try {
            await api.post("/admin/jobs", { ...form, example_links: form.example_links.filter((l) => l.trim()) });
            toast.success("Job posted — creators can now apply");
            setForm(EMPTY);
            setShowForm(false);
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : (detail || "Could not post the job"));
        } finally { setSaving(false); }
    };

    const decide = async (applicationId, decision) => {
        setBusy(applicationId);
        try {
            await api.post(`/admin/job-applications/${applicationId}/decision`, { decision });
            toast.success(decision === "approve" ? "Approved — welcome email sent to the creator" : "Application rejected");
            await load(active?.job?.job_id);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update the application");
        } finally { setBusy(""); }
    };

    const setStatus = async (jobId, status) => {
        try {
            await api.post(`/admin/jobs/${jobId}/status`, { status });
            await load(jobId);
        } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    };

    const remove = async (jobId) => {
        if (!window.confirm("Delete this job and all its applications? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/jobs/${jobId}`);
            toast.success("Job deleted");
            setActive(null);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
    };

    return (
        <DashboardShell role="admin" title="Jobs board" subtitle="Post briefs, review applicants, send creators to jobs"
            actions={
                <button data-testid="job-new-btn" onClick={() => setShowForm((v) => !v)} className="yc-btn-primary text-sm py-2 px-4">
                    <Plus className="w-4 h-4 mr-1" /> Post a job
                </button>
            }>
            {showForm && (
                <div className="yc-card p-6 space-y-4" data-testid="job-form">
                    <h3 className="font-heading text-xl font-bold">New job</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="yc-overline">Title *</label>
                            <input data-testid="job-title" className="yc-input mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Brand (hidden from creators until they win)</label>
                            <input data-testid="job-brand" className="yc-input mt-1" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="yc-overline">Description</label>
                            <textarea data-testid="job-description" rows={3} className="yc-input mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Deliverables</label>
                            <input data-testid="job-deliverables" className="yc-input mt-1" placeholder="e.g. 2 x 45s videos" value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Pay</label>
                            <input data-testid="job-pay" className="yc-input mt-1" placeholder="e.g. £250 per video" value={form.pay} onChange={(e) => setForm({ ...form, pay: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Deadline</label>
                            <input data-testid="job-deadline" className="yc-input mt-1" placeholder="e.g. 15 March" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label className="yc-overline">Example video links — show creators what we&apos;re looking for</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                            {[0, 1, 2].map((i) => (
                                <input
                                    key={`exl-${i}`}
                                    data-testid={`job-example-link-${i}`}
                                    className="yc-input"
                                    placeholder="https://www.instagram.com/reel/…"
                                    value={form.example_links[i]}
                                    onChange={(e) => {
                                        const next = [...form.example_links];
                                        next[i] = e.target.value;
                                        setForm({ ...form, example_links: next });
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="yc-overline">Specialisms</label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {specialisms.map((s) => (
                                <button key={s} type="button" data-testid={`job-spec-${s}`} onClick={() => toggleSpec(s)}
                                    className={`yc-badge ${form.specialisms.includes(s) ? "bg-yc-blue text-white" : "bg-white"}`}>{specLabel(s)}</button>
                            ))}
                        </div>
                    </div>
                    <button data-testid="job-submit" disabled={saving} onClick={create} className="yc-btn-primary">{saving ? "Posting…" : "Post job"}</button>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="yc-card-sm p-4 lg:col-span-1">
                    <h3 className="font-heading text-lg font-bold">Jobs ({jobs.length})</h3>
                    <div className="mt-4 space-y-2">
                        {jobs.map((j) => (
                            <button key={j.job_id} data-testid={`job-row-${j.job_id}`} onClick={() => openJob(j.job_id)}
                                className={`w-full text-left p-3 rounded-xl border-2 border-yc-ink transition-all ${active?.job?.job_id === j.job_id ? "bg-yc-blue text-white" : "bg-white"}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="font-semibold truncate">{j.title}</div>
                                    <span className={`yc-badge text-[10px] shrink-0 ${active?.job?.job_id === j.job_id ? "bg-white text-yc-ink" : j.status === "open" ? "bg-yc-cream" : "bg-yc-ink text-white"}`}>{j.status}</span>
                                </div>
                                <div className="text-xs opacity-75 flex items-center gap-1 mt-1">
                                    <Users className="w-3 h-3" /> {j.applications_total} applied · {j.applications_approved} approved
                                </div>
                            </button>
                        ))}
                        {jobs.length === 0 && <div className="text-center text-yc-ink/60 py-6 text-sm">No jobs posted yet.</div>}
                    </div>
                </div>

                {active && (
                    <div className="lg:col-span-2 space-y-6">
                        <div className="yc-card p-6">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="yc-overline">Job</div>
                                    <h2 className="font-heading text-2xl font-bold mt-1" data-testid="job-detail-title">{active.job.title}</h2>
                                    {active.job.brand_name && <div className="text-sm text-yc-ink/70 mt-1 flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {active.job.brand_name} <span className="text-[10px] text-yc-ink/50">(hidden from creators)</span></div>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <select data-testid="job-status-select" className="yc-input text-xs py-2 w-auto" value={active.job.status} onChange={(e) => setStatus(active.job.job_id, e.target.value)}>
                                        <option value="open">open</option>
                                        <option value="closed">closed</option>
                                        <option value="filled">filled</option>
                                    </select>
                                    <button data-testid="job-delete" onClick={() => remove(active.job.job_id)} className="flex items-center gap-1.5 text-xs font-bold text-yc-pink border-2 border-yc-pink rounded-full px-3 py-2 hover:bg-yc-pink hover:text-white transition-colors">
                                        <Trash2 className="w-4 h-4" /> Delete
                                    </button>
                                </div>
                            </div>
                            {active.job.description && <p className="text-sm mt-3 leading-relaxed">{active.job.description}</p>}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 text-sm">
                                {active.job.deliverables && <div><div className="yc-overline text-[10px]">Deliverables</div><div className="font-semibold">{active.job.deliverables}</div></div>}
                                {active.job.pay && <div><div className="yc-overline text-[10px]">Pay</div><div className="font-semibold">{active.job.pay}</div></div>}
                                {active.job.deadline && <div><div className="yc-overline text-[10px]">Deadline</div><div className="font-semibold">{active.job.deadline}</div></div>}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-3">
                                {(active.job.specialisms || []).map((s) => <span key={s} className="yc-badge bg-yc-cream text-[10px]">{specLabel(s)}</span>)}
                            </div>
                            {(active.job.example_links || []).length > 0 && (
                                <div className="mt-4">
                                    <div className="yc-overline text-[10px]">Example videos shown to creators</div>
                                    <div className="flex flex-col gap-1 mt-1 text-sm">
                                        {active.job.example_links.map((l, i) => (
                                            <a key={`jexl-${i}`} href={l} target="_blank" rel="noreferrer" className="text-yc-blue underline break-all">{l}</a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="yc-card p-6" data-testid="job-applicants-card">
                            <h3 className="font-heading text-xl font-bold">Applicants ({active.applicants.length})</h3>
                            <div className="mt-4 space-y-3">
                                {active.applicants.map((a) => (
                                    <div key={a.application_id} data-testid={`applicant-${a.application_id}`} className="border-2 border-yc-ink rounded-2xl p-4 bg-white">
                                        <div className="flex flex-wrap items-center gap-3">
                                            <div className="flex-1 min-w-[200px]">
                                                <div className="font-heading font-bold">{a.creator_name}</div>
                                                <div className="text-xs text-yc-ink/60">{a.contact_email}{a.location ? ` · ${a.location}` : ""}</div>
                                                <div className="text-xs text-yc-ink/50 mt-0.5" data-testid={`applicant-time-${a.application_id}`}>
                                                    Applied {new Date(a.applied_at).toLocaleString()}
                                                </div>
                                            </div>
                                            <span className={`yc-badge text-[10px] ${APP_BADGE[a.status] || "bg-yc-cream"}`} data-testid={`applicant-status-${a.application_id}`}>{a.status}</span>
                                            {a.status === "applied" && (
                                                <div className="flex gap-2">
                                                    <button data-testid={`applicant-approve-${a.application_id}`} disabled={busy === a.application_id} onClick={() => decide(a.application_id, "approve")} className="yc-btn-primary text-xs py-2 px-3 disabled:opacity-50">
                                                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                                                    </button>
                                                    <button data-testid={`applicant-reject-${a.application_id}`} disabled={busy === a.application_id} onClick={() => decide(a.application_id, "reject")} className="yc-btn-secondary text-xs py-2 px-3 disabled:opacity-50">
                                                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {((a.work_links || []).length > 0 || (a.proposal_links || []).length > 0) && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t-2 border-yc-ink/10 text-xs">
                                                <div data-testid={`applicant-work-links-${a.application_id}`}>
                                                    <div className="yc-overline text-[10px]">What they&apos;ve already done</div>
                                                    <div className="flex flex-col gap-1 mt-1">
                                                        {(a.work_links || []).map((l, i) => (
                                                            <a key={`wl-${i}`} href={l} target="_blank" rel="noreferrer" className="text-yc-blue underline break-all">{l}</a>
                                                        ))}
                                                        {(a.work_links || []).length === 0 && <span className="text-yc-ink/40">None provided</span>}
                                                    </div>
                                                </div>
                                                <div data-testid={`applicant-proposal-links-${a.application_id}`}>
                                                    <div className="yc-overline text-[10px]">What they propose to do</div>
                                                    <div className="flex flex-col gap-1 mt-1">
                                                        {(a.proposal_links || []).map((l, i) => (
                                                            <a key={`pl-${i}`} href={l} target="_blank" rel="noreferrer" className="text-yc-blue underline break-all">{l}</a>
                                                        ))}
                                                        {(a.proposal_links || []).length === 0 && <span className="text-yc-ink/40">None provided</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {active.applicants.length === 0 && <div className="text-center text-yc-ink/50 text-sm py-6">No applications yet.</div>}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardShell>
    );
};

export default AdminJobs;
